import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Collapse,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Stack,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Description as DocumentsIcon,
  Badge as EmployeesIcon,
  Schedule as TimesheetIcon,
  School as TrainingIcon,
  Quiz as QuizIcon,
  LibraryBooks as LibraryBooksIcon,
  Forum as ForumIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as UsersIcon,
  Security as RolesIcon,
  Groups as GroupsIcon,
  Settings as SettingsIcon,
  AccessTime as PunchCardIcon,
  History as ActivitiesIcon,
  Assessment as ReportsIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  PushPin,
  PushPinOutlined,
  Email as EmailExtractorIcon,
  HourglassEmpty as PendingIcon,
  Block as BlockedIcon,
  Rule as UserIdPatternIcon,
  Info as InfoIcon,
  FactCheck as AuditTrailIcon,
  Inventory2 as ImagesAdminIcon,
  GroupWork as BenchIcon,
  Share as SocialMediaIcon,
  Videocam as VideocamIcon,
  Chat as ChatIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  SupervisorAccount as MyTeamIcon,
  SupportAgent as SupportAgentIcon,
  Queue as QueueIcon,
  Add as AddIcon,
  HelpOutline as HelpOutlineIcon,
  Tune as TuneIcon,
  Public as ImmigrationIcon,
  FlightTakeoff as ProcessH1MenuIcon,
  CardTravel as ProcessB1MenuIcon,
  Flag as ProcessGCMenuIcon,
  VerifiedUser as EverifyExistingMenuIcon,
  PostAdd as EverifyNewMenuIcon,
  TrackChanges as OkrMenuIcon,
  AccountTree as EmployeeMapIcon,
  Title as TitleIcon,
  LocalOffer as LocalOfferIcon,
  EventAvailable as PtoLeaveIcon,
  PersonOutline as PersonOutlineIcon,
  Lock as LockIcon,
  Storage as SeedDataIcon,
  ViewList as AllStatusesIcon,
  Archive as ArchiveNavIcon,
  Backup as BackupZipNavIcon,
  DeleteOutline as DeletedDataNavIcon,
  Dns as DataAdminSectionIcon,
  CleaningServices as ClearDemoNavIcon,
  DeleteSweep as ClearSeedNavIcon,
  DeleteForever as DataDeletionAdminSectionIcon,
  Dangerous as ClearAllNavIcon,
  Visibility as DemoUsersViewIcon,
  ManageAccounts as UserSettingsNavIcon,
  Dns as SystemsInformationNavIcon,
  NoteAdd as UserCreationTemplateNavIcon,
  Backup as DbBackupAdminIcon,
  FolderCopy as DocumentBackupAdminIcon,
  SettingsBackupRestore as BackupAdministrationSectionIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  TravelExplore as QueryPageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode } from '../../context/ThemeContext';
import { useAppBranding } from '../../context/AppBrandingContext';
import { mergeBrandingTitleSx, DEFAULT_APP_TAGLINE_STYLE } from '../../utils/brandingStyles';
import { themeTemplateOptions } from '../../theme';
import api from '../../utils/api';
import customizationApi from '../../utils/customizationApi';
import {
  resolveSidebarNavColors,
  DEFAULT_MENU_TEMPLATE,
  DEFAULT_ITEMS_TEMPLATE,
} from '../../utils/sidebarNavColors';

function navSectionSurfaceSx(theme, accentHex) {
  const isDark = theme.palette.mode === 'dark';
  return {
    borderRadius: 2,
    px: 1.25,
    py: 0.75,
    mb: 1.25,
    borderLeft: '3px solid',
    borderLeftColor: accentHex,
    bgcolor: alpha(accentHex, isDark ? 0.14 : 0.09),
  };
}

/** When set (e.g. `/training-ui`), open training pages on the separate training SPA (same host, same JWT). */
const externalTrainingBase = (process.env.REACT_APP_EXTERNAL_TRAINING_UI || '').trim().replace(/\/$/, '');

const Sidebar = ({
  drawerWidthExpanded = 280,
  desktopDrawerWidth,
  navCompact,
  sidebarPinned,
  onToggleSidebarPin,
  onToggleSidebarCollapsed,
  mobileOpen,
  handleDrawerToggle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const [navTheme, setNavTheme] = React.useState(() =>
    resolveSidebarNavColors(DEFAULT_MENU_TEMPLATE, DEFAULT_ITEMS_TEMPLATE)
  );
  const sectionColors = navTheme.colors;
  const itemStyle = navTheme.itemStyle;
  const dataClearanceActive = location.pathname.startsWith('/admin/data-clearance');
  const demoUsersViewActive = location.pathname.startsWith('/admin/demo-users-view');
  const [showDemoDataInUi, setShowDemoDataInUi] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get('/settings/sidebar-appearance', { skipErrorToast: true });
        const d = r.data?.data;
        if (cancelled || !d) return;
        setNavTheme(
          resolveSidebarNavColors(
            d.menuColorTemplate || DEFAULT_MENU_TEMPLATE,
            d.menuItemsColorTemplate || DEFAULT_ITEMS_TEMPLATE
          )
        );
        const dv = d.showDemoDataInUi;
        setShowDemoDataInUi(dv !== false && dv !== 'false');
      } catch {
        /* keep defaults */
      }
    };
    load();
    const on = () => load();
    window.addEventListener('erp4c-sidebar-appearance-updated', on);
    return () => {
      cancelled = true;
      window.removeEventListener('erp4c-sidebar-appearance-updated', on);
    };
  }, []);
  const { hasModuleAccess, isAdmin, hasPermission, user, loading: authLoading } = useAuth();
  const canViewAllActivities = isAdmin || hasPermission('all_activities_view');
  const {
    logoImageUrl,
    displayCompanyName,
    sidebarTagline,
    footerTextShort,
    companyNameStyle,
    companyTaglineStyleApp
  } = useAppBranding();
  const [cameraMenuEligible, setCameraMenuEligible] = React.useState(null);
  const {
    themeMode,
    setThemeMode,
    themeBrightness,
    setThemeBrightness,
    themeContrast,
    setThemeContrast,
    uiFontScale,
    setUiFontScale,
    resetAppearance,
  } = useThemeMode();
  const [appearanceOpen, setAppearanceOpen] = React.useState(false);
  const [menuSearch, setMenuSearch] = React.useState('');
  const [adminOpen, setAdminOpen] = React.useState(false);
  const [reportsOpen, setReportsOpen] = React.useState(false);
  const [reportsUserOpen, setReportsUserOpen] = React.useState(false);
  const [reportsManagerOpen, setReportsManagerOpen] = React.useState(false);
  const [reportsConfidentialOpen, setReportsConfidentialOpen] = React.useState(false);
  const [trainingOpen, setTrainingOpen] = React.useState(false);
  const [myItemsOpen, setMyItemsOpen] = React.useState(false);
  const [activityOpen, setActivityOpen] = React.useState(false);
  const [generalItemsOpen, setGeneralItemsOpen] = React.useState(false);
  const [emailExtractorOpen, setEmailExtractorOpen] = React.useState(false);
  const [extractedDataOpen, setExtractedDataOpen] = React.useState(false);
  const [benchSalesOpen, setBenchSalesOpen] = React.useState(false);
  const [digitalMediaOpen, setDigitalMediaOpen] = React.useState(false);
  const [myTeamOpen, setMyTeamOpen] = React.useState(false);
  const [myTeamReporteeCount, setMyTeamReporteeCount] = React.useState(0);
  const [helpDeskOpen, setHelpDeskOpen] = React.useState(false);
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [immigrationOpen, setImmigrationOpen] = React.useState(false);
  const [okrMenuOpen, setOkrMenuOpen] = React.useState(false);
  const [okrSettingsMenuOpen, setOkrSettingsMenuOpen] = React.useState(false);
  const [employeeSettingsOpen, setEmployeeSettingsOpen] = React.useState(false);
  const [trainingAdminOpen, setTrainingAdminOpen] = React.useState(false);
  const [userSettingsMenuOpen, setUserSettingsMenuOpen] = React.useState(false);
  const [systemsInformationMenuOpen, setSystemsInformationMenuOpen] = React.useState(false);
  const [backupAdminMenuOpen, setBackupAdminMenuOpen] = React.useState(false);
  const [dataDeletionAdminMenuOpen, setDataDeletionAdminMenuOpen] = React.useState(false);
  const [allStatusesOpen, setAllStatusesOpen] = React.useState(false);
  const [seedDataOpen, setSeedDataOpen] = React.useState(false);
  const [dataAdminOpen, setDataAdminOpen] = React.useState(false);
  const [customizeOpen, setCustomizeOpen] = React.useState(false);
  const [customizationGate, setCustomizationGate] = React.useState(null);

  React.useEffect(() => {
    if (!isAdmin) {
      setCustomizationGate(null);
      return undefined;
    }
    let cancelled = false;
    customizationApi
      .get('/v1/admin/ui-gate')
      .then((r) => {
        if (!cancelled) setCustomizationGate(r.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setCustomizationGate({ customizationApiAvailable: false, showCustomizationMenus: false });
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  React.useEffect(() => {
    const onRefresh = () => {
      if (!isAdmin) return;
      customizationApi
        .get('/v1/admin/ui-gate')
        .then((r) => setCustomizationGate(r.data?.data || null))
        .catch(() => setCustomizationGate({ customizationApiAvailable: false, showCustomizationMenus: false }));
    };
    window.addEventListener('erp4c-customization-gate-refresh', onRefresh);
    return () => window.removeEventListener('erp4c-customization-gate-refresh', onRefresh);
  }, [isAdmin]);

  React.useEffect(() => {
    if (authLoading || !user) {
      setCameraMenuEligible(null);
      return;
    }
    if (!hasModuleAccess('camera')) {
      setCameraMenuEligible(null);
      return;
    }
    if (isAdmin) {
      setCameraMenuEligible(true);
      return;
    }
    let cancelled = false;
    api
      .get('/camera/eligibility', { skipErrorToast: true })
      .then((r) => {
        if (!cancelled) setCameraMenuEligible(Boolean(r.data?.data?.eligible));
      })
      .catch(() => {
        if (!cancelled) setCameraMenuEligible(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, user, isAdmin, hasModuleAccess]);

  const showManagerReportNav = hasModuleAccess('reports');
  const showConfidentialIdleNav = hasPermission('reports_confidential');

  const myTeamNavItems = [
    { title: 'Camera Stream', path: '/my-team/camera', module: 'camera', icon: <VideocamIcon /> },
    { title: 'My Team Jobs', path: '/my-team/jobs', module: 'jobs', icon: <WorkIcon /> },
    { title: 'My Team Dashboard', path: '/my-team/dashboard', module: 'jobs', icon: <DashboardIcon /> },
    { title: 'My reportees (HR)', path: '/my-team/reportees', module: 'jobs', icon: <PeopleIcon /> },
    { title: 'My Team OKRs', path: '/my-team/okrs', module: 'okr', icon: <OkrMenuIcon /> },
    { title: 'My Team OKRs Scoreboard', path: '/my-team/okr-scoreboard', module: 'okr', icon: <OkrMenuIcon /> },
    { title: 'My Team Bench', path: '/my-team/bench', module: 'bench_sales', icon: <BenchIcon /> },
    {
      title: "My team's tickets",
      path: '/helpdesk/team',
      module: 'helpdesk',
      icon: <SupportAgentIcon />,
    },
  ];

  const immigrationNavItems = [
    { title: 'Process H1', path: '/immigration/process-h1', module: 'immigration', icon: <ProcessH1MenuIcon /> },
    { title: 'Process B1', path: '/immigration/process-b1', module: 'immigration', icon: <ProcessB1MenuIcon /> },
    { title: 'Process GC', path: '/immigration/process-gc', module: 'immigration', icon: <ProcessGCMenuIcon /> },
    { title: 'E-Verify Existing', path: '/immigration/everify-existing', module: 'immigration', icon: <EverifyExistingMenuIcon /> },
    { title: 'E-Verify New', path: '/immigration/everify-new', module: 'immigration', icon: <EverifyNewMenuIcon /> },
  ];

  const helpDeskNavItems = [
    { title: 'New ticket', path: '/helpdesk/new', module: 'helpdesk', icon: <AddIcon /> },
    { title: 'Job Support Seeker', path: '/helpdesk/job-support-seeker', module: 'helpdesk', icon: <WorkIcon /> },
    {
      title: 'Job Support Provider',
      path: '/helpdesk/job-support-provider',
      module: 'helpdesk',
      icon: <PeopleIcon />,
    },
    { title: 'My tickets', path: '/helpdesk/my', module: 'helpdesk', icon: <SupportAgentIcon /> },
    {
      title: 'Support queue',
      path: '/helpdesk/queue',
      module: 'helpdesk',
      icon: <QueueIcon />,
      needsSupport: true,
    },
  ];

  /** Product help (README / videos / walkthrough) — separate from Help Desk tickets. */
  const helpNavItems = [
    { title: 'ERP4C Product Help', path: '/help/product', module: null, icon: <HelpOutlineIcon /> },
  ];

  React.useEffect(() => {
    if (authLoading || !user) {
      setMyTeamReporteeCount(0);
      return undefined;
    }
    let cancelled = false;
    api
      .get('/my-team/context', { skipErrorToast: true })
      .then((r) => {
        if (cancelled) return;
        const d = r.data?.data;
        setMyTeamReporteeCount(d?.reportee_count ?? 0);
      })
      .catch((err) => {
        if (!cancelled) {
          setMyTeamReporteeCount(0);
          const st = err?.response?.status;
          if (st === 404) {
            console.warn(
              '[My Team] /api/my-team not found — deploy a backend image that includes the my-team routes, then restart pods.'
            );
          } else if (err?.code === 'ERR_NETWORK') {
            console.warn('[My Team] Network error calling /my-team/context — check API URL and port-forward.');
          } else if (st) {
            console.warn('[My Team] /my-team/context failed:', st, err?.response?.data?.message || '');
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, user]);

  const benchSalesNavAllowed =
    hasModuleAccess('bench_sales') || hasModuleAccess('contacts');

  const hasAnyMyTeamModule = myTeamNavItems.some((item) =>
    item.path === '/my-team/bench' ? benchSalesNavAllowed : hasModuleAccess(item.module)
  );
  /** Admins always see the block (QA/demo). Others only when they have direct reports. */
  const showMyTeamSection = isAdmin || myTeamReporteeCount > 0;

  const userReportNavItems = [
    { title: 'My Idle Time', icon: <PersonOutlineIcon />, path: '/reports/my-idle-time' }
  ];
  const managerReportNavItems = [
    { title: 'Punch Card Report', icon: <PunchCardIcon />, path: '/reports/punchcard' },
    { title: 'Jobs Report', icon: <WorkIcon />, path: '/reports/jobs' },
    { title: 'Activity Report', icon: <ActivitiesIcon />, path: '/reports/activities' },
    ...(myTeamReporteeCount > 0
      ? [{ title: 'My Team Idle Report', icon: <MyTeamIcon />, path: '/reports/my-team-idle-time' }]
      : [])
  ];
  const confidentialReportNavItems = [
    { title: 'Emp Idle Time', icon: <LockIcon />, path: '/reports/idle-time' }
  ];

  // My Items - personal items for logged in user
  const myItems = [
    {
      title: 'My Punch Card',
      icon: <PunchCardIcon />,
      path: '/punchcard',
      module: 'dashboard'  // Available to all authenticated users
    },
    {
      title: 'My Activities',
      icon: <ActivitiesIcon />,
      path: '/activities',
      module: 'dashboard'  // Available to all authenticated users
    },
    {
      title: 'My PTO (Apply Leave)',
      icon: <PtoLeaveIcon />,
      path: '/my-items/pto',
      module: 'dashboard'
    },
    {
      title: 'My account status',
      icon: <BusinessIcon />,
      path: '/my-items/my-account-status',
      module: 'accounts'
    },
    {
      title: 'My Training',
      icon: <TrainingIcon />,
      path: '/training/my-training',
      module: 'training'
    },
    {
      title: 'My camera',
      icon: <VideocamIcon />,
      path: '/camera',
      module: 'camera'
    },
    {
      title: 'Chat',
      icon: <ChatIcon />,
      path: '/chat',
      module: 'chat'
    },
    {
      title: 'My Tickets',
      icon: <SupportAgentIcon />,
      path: '/helpdesk/my',
      module: 'helpdesk'
    },
    {
      title: 'My OKRs',
      icon: <OkrMenuIcon />,
      path: '/my-items/okrs',
      module: 'okr'
    },
    {
      title: 'My OKR Scoreboard',
      icon: <OkrMenuIcon />,
      path: '/my-items/okr-scoreboard',
      module: 'okr'
    },
    {
      title: 'My Employee Map',
      icon: <EmployeeMapIcon />,
      path: '/my-items/employee-map',
      module: 'okr'
    },
    {
      title: 'My Benches',
      icon: <BenchIcon />,
      path: '/my-items/my-benches',
      module: 'bench_sales'
    },
  ];

  // General Items - main application features
  const generalItems = [
    {
      title: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      module: 'dashboard'
    },
    {
      title: 'Home Page',
      icon: <HomeIcon />,
      path: '/',
      publicSite: true,
    },
    {
      title: 'Query Page',
      icon: <QueryPageIcon />,
      path: '/query',
      publicSite: true,
    },
    {
      title: 'Contacts',
      icon: <PeopleIcon />,
      path: '/contacts',
      module: 'contacts'
    },
    {
      title: 'Accounts',
      icon: <BusinessIcon />,
      path: '/accounts',
      module: 'accounts'
    },
    {
      title: 'Job Requirements',
      icon: <WorkIcon />,
      path: '/jobs',
      module: 'jobs'
    },
    {
      title: 'Documents',
      icon: <DocumentsIcon />,
      path: '/documents',
      module: 'documents'
    },
    {
      title: 'Timesheets',
      icon: <TimesheetIcon />,
      path: '/timesheets',
      module: 'timesheets'
    }
  ];

  const okrNavItems = [
    { title: 'OKR setup', path: '/okr/objectives', module: 'okr' },
    { title: 'KR+Cat(%)+Target(Nos)', path: '/okr/key-results', module: 'okr' },
    { title: 'Key Result Categories', path: '/okr/categories', module: 'okr' },
    { title: 'Employee Map', path: '/okr/employee-map', module: 'okr' }
  ];

  const okrSettingsNavItems = [
    { title: 'OKR Target Frequency Settings', path: '/okr/settings/target-frequency', module: 'okr' }
  ];

  // Email Extractor Items
  const emailExtractorItems = [
    {
      title: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/email-extractor'
    },
    {
      title: 'Extracted Emails',
      icon: <EmailExtractorIcon />,
      path: '/email-extractor/emails'
    },
    {
      title: 'PST Upload',
      icon: <DocumentsIcon />,
      path: '/email-extractor/pst'
    },
    {
      title: 'Email Providers',
      icon: <BusinessIcon />,
      path: '/email-extractor/providers'
    },
    {
      title: 'Manual Import',
      icon: <UsersIcon />,
      path: '/email-extractor/import'
    },
    {
      title: 'Domain Exclusions',
      icon: <SettingsIcon />,
      path: '/email-extractor/domains'
    },
    {
      title: 'History',
      icon: <ActivitiesIcon />,
      path: '/email-extractor/history'
    }
  ];

  const extractedDataItems = [
    {
      title: 'GSA Companies',
      icon: <BusinessIcon />,
      path: '/extracted-data/gsa-companies'
    },
    {
      title: 'US Universities',
      icon: <LibraryBooksIcon />,
      path: '/extracted-data/us-universities'
    }
  ];

  // Bench Sales Items
  const benchSalesItems = [
    {
      title: 'All Benches',
      icon: <BenchIcon />,
      path: '/bench-sales'
    },
    {
      title: 'Available Candidates',
      icon: <UsersIcon />,
      path: '/bench-sales/available'
    }
  ];

  // Digital Media Items
  const digitalMediaItems = [
    {
      title: 'Social Media Registration',
      icon: <SocialMediaIcon />,
      path: '/digital-media/social-registration'
    },
    {
      title: 'My Accounts',
      icon: <BusinessIcon />,
      path: '/digital-media/my-accounts'
    }
  ];

  const userSettingsNavItems = [
    { title: 'Users', icon: <UsersIcon />, path: '/admin/users', module: 'users' },
    { title: 'Pending Approvals', icon: <PendingIcon />, path: '/admin/pending-approvals', module: 'users' },
    { title: 'PTO requests', icon: <PtoLeaveIcon />, path: '/admin/pto-requests', module: 'users' },
    { title: 'User ID Pattern', icon: <UserIdPatternIcon />, path: '/admin/user-id-patterns', module: 'users' },
    { title: 'App Roles', icon: <RolesIcon />, path: '/admin/roles', module: 'roles' },
    { title: 'Groups', icon: <GroupsIcon />, path: '/admin/groups', module: 'groups' },
    { title: 'User Access Map', icon: <EmployeeMapIcon />, path: '/admin/user-access-map', module: 'users' },
    {
      title: 'User Creation Template',
      icon: <UserCreationTemplateNavIcon />,
      path: '/admin/user-creation-templates',
      module: 'users',
    },
  ];

  const systemsInformationNavItems = [
    { title: 'System Info', icon: <InfoIcon />, path: '/admin/system-info', module: 'users' },
    { title: 'Audit trail', icon: <AuditTrailIcon />, path: '/admin/audit-trail', module: 'users' },
    { title: 'Images Administration', icon: <ImagesAdminIcon />, path: '/admin/images', adminOnly: true },
  ];

  const backupAdminNavItems = [
    { title: 'DB Backup Administration', icon: <DbBackupAdminIcon />, path: '/admin/db-backup' },
    { title: 'Document Backup Administration', icon: <DocumentBackupAdminIcon />, path: '/admin/document-backup' },
  ];

  const dataDeletionAdminNavItems = [
    {
      title: 'Contact deletion',
      path: '/admin/all-statuses/contact-deletion',
      ItemIcon: PeopleIcon,
      module: 'users',
    },
    {
      title: 'Account deletion',
      path: '/admin/all-statuses/account-deletion',
      ItemIcon: BusinessIcon,
      module: 'users',
    },
    {
      title: 'Document deletion',
      path: '/admin/all-statuses/document-deletion',
      ItemIcon: DocumentsIcon,
      module: 'users',
    },
    {
      title: 'DB Backup Deletion',
      path: '/admin/all-statuses/db-backup-deletion',
      ItemIcon: DbBackupAdminIcon,
      adminOnly: true,
    },
  ];

  const dataDeletionAdminItemVisible = (item) => {
    if (item.adminOnly) return isAdmin;
    if (item.module) return isAdmin || hasModuleAccess(item.module);
    return false;
  };
  const showDataDeletionAdminSection = dataDeletionAdminNavItems.some(dataDeletionAdminItemVisible);
  const visibleDataDeletionAdminItems = dataDeletionAdminNavItems.filter(dataDeletionAdminItemVisible);

  const adminItems = [
    {
      title: 'Settings',
      icon: <SettingsIcon />,
      path: '/admin/settings',
      module: 'users',
    },
    {
      title: 'Camera monitor',
      icon: <VideocamIcon />,
      path: '/camera/admin',
      permission: 'camera_watch',
    },
  ];

  const userSettingsNavItemVisible = (item) => isAdmin || hasModuleAccess(item.module);
  const showUserSettingsNavSection = userSettingsNavItems.some(userSettingsNavItemVisible);

  const systemsInformationNavItemVisible = (item) => {
    if (item.adminOnly) return isAdmin;
    return isAdmin || hasModuleAccess(item.module);
  };
  const showSystemsInformationNavSection = systemsInformationNavItems.some(systemsInformationNavItemVisible);

  const employeeSettingsItems = [
    {
      title: 'Employee Job Roles',
      icon: <WorkIcon />,
      path: '/admin/employee-settings/job-roles',
      module: 'users'
    },
    {
      title: 'Employee Levels',
      icon: <TitleIcon />,
      path: '/admin/employee-settings/titles',
      module: 'users'
    },
    {
      title: 'Employee Offered Title',
      icon: <LocalOfferIcon />,
      path: '/admin/employee-settings/offered-titles',
      module: 'users'
    }
  ];

  const allStatusesItems = [
    {
      title: 'Domain Status',
      path: '/admin/domain-status',
      ItemIcon: BlockedIcon,
      module: 'users',
    },
    {
      title: 'Account Duplicate Requests',
      path: '/admin/account-duplicate-requests',
      ItemIcon: BusinessIcon,
      permissionsAny: ['accounts_approve', 'accounts_modify'],
    },
  ];

  const allStatusesItemVisible = (item) => {
    if (item.adminOnly) return isAdmin;
    if (item.permissionsAny) {
      return isAdmin || item.permissionsAny.some((p) => hasPermission(p));
    }
    if (item.module) {
      return isAdmin || hasModuleAccess(item.module);
    }
    return false;
  };

  const showAllStatusesSection = allStatusesItems.some(allStatusesItemVisible);
  const visibleAllStatusesItems = allStatusesItems.filter(allStatusesItemVisible);

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      handleDrawerToggle();
    }
  };

  const openTrainingPath = (path) => {
    if (externalTrainingBase && path.startsWith('/training')) {
      if (isMobile) handleDrawerToggle();
      window.location.assign(`${externalTrainingBase}${path}`);
      return;
    }
    handleNavigation(path);
  };

  const isActive = (path) => {
    if (!path) return false;
    const [pathnamePart, queryPart] = path.split('?');
    const menuQs = queryPart ? new URLSearchParams(queryPart) : null;

    // Exact public marketing routes (avoid matching every path via startsWith('/'))
    if (pathnamePart === '/') {
      return location.pathname === '/';
    }
    if (pathnamePart === '/query') {
      return location.pathname === '/query' || location.pathname.startsWith('/query/');
    }

    if (pathnamePart === '/dashboard') {
      const dash = location.pathname === '/dashboard';
      if (!dash) return false;
      return true;
    }

    if (!location.pathname.startsWith(pathnamePart)) return false;

    if (pathnamePart === '/jobs') {
      return location.pathname === '/jobs';
    }

    if (!menuQs) return true;
    const locQs = new URLSearchParams(location.search.replace(/^\?/, ''));
    for (const [k, v] of menuQs.entries()) {
      if (locQs.get(k) !== v) return false;
    }
    return true;
  };

  const searchableMenuEntries = React.useMemo(() => {
    if (authLoading || !user) return [];
    const seen = new Set();
    const rows = [];
    const add = (title, path) => {
      if (!path || seen.has(path)) return;
      seen.add(path);
      rows.push({ title, path });
    };

    generalItems.forEach((item) => {
      const show =
        item.publicSite || item.path === '/dashboard'
          ? Boolean(user) && !authLoading
          : hasModuleAccess(item.module);
      if (show) add(item.title, item.path);
    });

    myItems.forEach((item) => {
      if (item.path === '/my-items/my-benches') {
        if (!benchSalesNavAllowed) return;
      } else if (!hasModuleAccess(item.module)) return;
      if (item.path === '/camera' && !isAdmin && cameraMenuEligible !== true) return;
      add(item.title, item.path);
    });

    if (canViewAllActivities) {
      add('All activities', '/activities/all');
    }

    if (showMyTeamSection) {
      myTeamNavItems.forEach((item) => {
        if (item.path === '/my-team/bench') {
          if (!benchSalesNavAllowed) return;
        } else if (!hasModuleAccess(item.module)) return;
        add(item.title, item.path);
      });
    }

    if (hasModuleAccess('okr')) {
      okrNavItems.forEach((item) => {
        if (hasModuleAccess(item.module)) add(item.title, item.path);
      });
      okrSettingsNavItems.forEach((item) => {
        if (hasModuleAccess(item.module)) add(item.title, item.path);
      });
    }

    if (benchSalesNavAllowed) {
      benchSalesItems.forEach((item) => add(item.title, item.path));
    }

    helpNavItems.forEach((item) => {
      if (hasModuleAccess(item.module)) add(item.title, item.path);
    });

    helpDeskNavItems.forEach((item) => {
      if (!hasModuleAccess(item.module)) return;
      if (
        item.needsSupport &&
        !isAdmin &&
        !hasPermission('helpdesk_support') &&
        !hasPermission('helpdesk_modify')
      ) {
        return;
      }
      add(item.title, item.path);
    });

    if (hasModuleAccess('immigration')) {
      immigrationNavItems.forEach((item) => {
        if (hasModuleAccess(item.module)) add(item.title, item.path);
      });
    }

    if (hasModuleAccess('training')) {
      add('Browse Catalog', '/training/catalog');
      add('My Training', '/training/my-training');
      add('Instructor Dashboard', '/training/instructor');
      add('Standalone exams', '/training/standalone-exams');
      add('Question bank', '/training/question-bank');
      add('Group discussions', '/training/group-discussions');
      if (hasPermission('training_admin') || hasPermission('training_add')) {
        add('Manage Training', '/training/admin');
        add('YouTube Library', '/training/admin?tab=youtube-library');
        add('Exams', '/training/admin?tab=exams');
      }
    }

    userReportNavItems.forEach((item) => add(item.title, item.path));
    if (showManagerReportNav) {
      managerReportNavItems.forEach((item) => add(item.title, item.path));
    }
    if (showConfidentialIdleNav) {
      confidentialReportNavItems.forEach((item) => add(item.title, item.path));
    }

    if (hasModuleAccess('email_extractor')) {
      emailExtractorItems.forEach((item) => add(item.title, item.path));
    }
    if (hasModuleAccess('extracted_data')) {
      extractedDataItems.forEach((item) => add(item.title, item.path));
    }

    digitalMediaItems.forEach((item) => add(item.title, item.path));

    if (isAdmin) {
      backupAdminNavItems.forEach((item) => add(item.title, item.path));
    }

    dataDeletionAdminNavItems.forEach((item) => {
      if (!dataDeletionAdminItemVisible(item)) return;
      add(item.title, item.path);
    });

    const showAdministrationSection =
      isAdmin ||
      hasModuleAccess('users') ||
      hasModuleAccess('roles') ||
      hasModuleAccess('groups') ||
      hasPermission('camera_watch') ||
      hasPermission('accounts_approve') ||
      hasPermission('accounts_modify') ||
      hasPermission('helpdesk_admin');

    if (showAdministrationSection) {
      add('Training Administration', '/admin/training-administration');
      if (isAdmin) add('Help Setup', '/admin/help-setup');
      if (hasModuleAccess('users')) {
        employeeSettingsItems.forEach((item) => add(item.title, item.path));
      }
      if (isAdmin) {
        add('Application settings', '/admin/customize/application-settings');
        if (customizationGate?.showCustomizationMenus) {
          add('Add fields', '/admin/customize/fields');
          add('Module relationships', '/admin/customize/relationships');
        }
      }
      allStatusesItems.forEach((item) => {
        if (allStatusesItemVisible(item)) add(item.title, item.path);
      });
      if (isAdmin) {
        add('Employees', '/employees');
      }
      userSettingsNavItems.forEach((item) => {
        if (userSettingsNavItemVisible(item)) add(item.title, item.path);
      });
      systemsInformationNavItems.forEach((item) => {
        if (systemsInformationNavItemVisible(item)) add(item.title, item.path);
      });
      adminItems.forEach((item) => {
        if (item.permissionsAny) {
          if (!isAdmin && !item.permissionsAny.some((p) => hasPermission(p))) return;
        } else if (item.permission) {
          if (!hasPermission(item.permission)) return;
        } else if (!isAdmin && !hasModuleAccess(item.module)) return;
        add(item.title, item.path);
      });
    }

    if (isAdmin || hasPermission('helpdesk_admin')) {
      if (isAdmin) {
        add('Seed catalog', '/admin/seed-data');
      }
      if (isAdmin || hasPermission('helpdesk_admin')) {
        add('Help desk severities', '/admin/helpdesk-severities');
      }
      if (isAdmin) {
        add('Deleted Data', '/admin/deleted-data');
        add('Archived Data', '/admin/archived-data');
        add('Backed Up Data', '/admin/data-backups');
        if (showDemoDataInUi) {
          add('Demo/Test Users Actual View', '/admin/demo-users-view');
          add('Clear Demo/Test Data', '/admin/data-clearance#demo');
        }
        add('Clear Seed Data', '/admin/data-clearance#seed');
        add('Clear All Data', '/admin/data-clearance#all');
      }
    }

    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nav configs are large inline arrays (new identity each render); deps list auth/module/state gates only
  }, [
    authLoading,
    user,
    isAdmin,
    cameraMenuEligible,
    myTeamReporteeCount,
    customizationGate,
    showDemoDataInUi,
    benchSalesNavAllowed,
    showMyTeamSection,
    canViewAllActivities,
    showManagerReportNav,
    showConfidentialIdleNav,
    hasModuleAccess,
    hasPermission,
  ]);

  const searchMenuHits = React.useMemo(() => {
    const t = menuSearch.trim();
    if (!t) return [];
    const q = t.toLowerCase();
    return searchableMenuEntries
      .filter(
        (row) => row.title.toLowerCase().includes(q) || row.path.toLowerCase().includes(q)
      )
      .slice(0, 80);
  }, [menuSearch, searchableMenuEntries]);

  const showMenuSearchResults = Boolean(menuSearch.trim()) && (!navCompact || isMobile);

  const drawerPaperExtraSx = {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shortest,
    }),
    ...(navCompact && !isMobile
      ? {
          overflowX: 'hidden',
          '& .MuiListItemText-root': { display: 'none' },
          '& .MuiListItemButton-root': { justifyContent: 'center', px: 0.75 },
          '& .MuiListItemIcon-root': { minWidth: 0, mr: 0, justifyContent: 'center' },
          '& .sidebar-nav-expand-chevron': { display: 'none !important' },
        }
      : {}),
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo + tenant name (Settings → Branding) — click → Dashboard */}
      <Box
        sx={{
          p: navCompact && !isMobile ? 1 : 3,
          pb: navCompact && !isMobile ? 1 : 2,
          cursor: 'pointer',
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => handleNavigation('/dashboard')}
        role="link"
        aria-label="Go to Dashboard"
        title="Go to Dashboard"
      >
        {logoImageUrl ? (
          <Box
            component="img"
            src={logoImageUrl}
            alt=""
            sx={{
              maxHeight: navCompact && !isMobile ? 32 : 48,
              maxWidth: '100%',
              objectFit: 'contain',
              mb: navCompact && !isMobile ? 0.5 : 1.5,
              display: 'block',
              mx: navCompact && !isMobile ? 'auto' : undefined,
            }}
          />
        ) : null}
        {!navCompact || isMobile ? (
          <>
            <Typography
              variant="h6"
              sx={mergeBrandingTitleSx(
                {
                  fontWeight: 700,
                  fontFamily: '"Georgia", "Cambria", "Times New Roman", serif',
                  letterSpacing: '0.02em',
                  lineHeight: 1.25,
                  ...(logoImageUrl
                    ? { color: 'text.primary' }
                    : {
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }),
                },
                companyNameStyle
              )}
            >
              {displayCompanyName}
            </Typography>
            {sidebarTagline ? (
              <Typography
                variant="caption"
                sx={mergeBrandingTitleSx(
                  {
                    display: 'block',
                    mt: 0.75,
                    fontStyle: 'italic',
                    lineHeight: 1.4,
                    color: 'text.primary',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    whiteSpace: 'normal'
                  },
                  { ...DEFAULT_APP_TAGLINE_STYLE, ...(companyTaglineStyleApp || {}) }
                )}
              >
                {sidebarTagline}
              </Typography>
            ) : null}
          </>
        ) : null}
      </Box>

      <Divider sx={{ mx: navCompact && !isMobile ? 0.5 : 2 }} />

      {!isMobile && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.25} sx={{ px: 0.75, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <Tooltip title={sidebarPinned ? 'Unpin sidebar (then you can collapse to icons)' : 'Pin sidebar open'}>
            <IconButton
              size="small"
              onClick={onToggleSidebarPin}
              color={sidebarPinned ? 'primary' : 'default'}
              aria-label={sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar open'}
            >
              {sidebarPinned ? <PushPin sx={{ fontSize: 20 }} /> : <PushPinOutlined sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title={sidebarPinned ? 'Unpin first to collapse' : navCompact ? 'Expand sidebar' : 'Collapse to icons'}>
            <span>
              <IconButton
                size="small"
                onClick={onToggleSidebarCollapsed}
                disabled={sidebarPinned}
                aria-label="Toggle sidebar width"
              >
                {navCompact ? <ChevronRight sx={{ fontSize: 20 }} /> : <ChevronLeft sx={{ fontSize: 20 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}

      {/* Main Menu: General → My Items → My Team, then OKRs, Bench, Help Desk, … */}
      <List component="div" disablePadding sx={{ px: 2, py: 1, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
        {(!navCompact || isMobile) && (
          <Box sx={{ flexShrink: 0, mb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search menu…"
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: menuSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear menu search"
                      onClick={() => setMenuSearch('')}
                      edge="end"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        )}
        {showMenuSearchResults ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {searchMenuHits.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 0.5 }}>
                No menu items match your search.
              </Typography>
            ) : (
              searchMenuHits.map((row) => (
                <ListItem key={row.path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      if (row.path.startsWith('/training')) {
                        openTrainingPath(row.path);
                      } else {
                        handleNavigation(row.path);
                      }
                      setMenuSearch('');
                    }}
                    sx={{ borderRadius: 2, py: 1, alignItems: 'flex-start' }}
                  >
                    <ListItemText
                      primary={row.title}
                      secondary={row.path}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        sx: { wordBreak: 'break-all', display: 'block', mt: 0.25 },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </Box>
        ) : (
          <>
        <Box sx={navSectionSurfaceSx(theme, sectionColors.general)}>
        {/* General Items (first; Dashboard is first link inside) */}
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => setGeneralItemsOpen(!generalItemsOpen)}
            sx={{
              borderRadius: 2,
              py: 1,
              backgroundColor: generalItems.some((item) => isActive(item.path))
                ? alpha(sectionColors.general, 0.12)
                : 'transparent',
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: sectionColors.general }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText
              primary="General Items"
              primaryTypographyProps={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: sectionColors.general,
              }}
            />
            {generalItemsOpen ? (
              <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.general }} />
            ) : (
              <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.general }} />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={generalItemsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {generalItems.map((item) => {
              const showGeneralItem =
                item.publicSite || item.path === '/dashboard'
                  ? Boolean(user) && !authLoading
                  : hasModuleAccess(item.module);
              if (!showGeneralItem) return null;
              const c = sectionColors.general;
              const act = isActive(item.path);
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                      '&:hover': {
                        backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: act ? 600 : 500,
                        color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
        </Box>

        <Box sx={navSectionSurfaceSx(theme, sectionColors.myItems)}>
        {/* My Items */}
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => setMyItemsOpen(!myItemsOpen)}
            sx={{
              borderRadius: 2,
              py: 1,
              backgroundColor: myItems.some((item) => {
                if (item.path === '/activities') {
                  const b = location.pathname.replace(/\/$/, '') || '/';
                  return b === '/activities';
                }
                if (item.path === '/my-items/pto') {
                  return location.pathname.startsWith('/my-items/pto');
                }
                if (item.path === '/my-items/my-account-status') {
                  return location.pathname.startsWith('/my-items/my-account-status');
                }
                return isActive(item.path);
              })
                ? alpha(sectionColors.myItems, 0.12)
                : 'transparent',
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: sectionColors.myItems }}>
              <PunchCardIcon />
            </ListItemIcon>
            <ListItemText
              primary="My Items"
              primaryTypographyProps={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: sectionColors.myItems,
              }}
            />
            {myItemsOpen ? (
              <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.myItems }} />
            ) : (
              <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.myItems }} />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={myItemsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {myItems.map((item) => {
              if (item.path === '/my-items/my-benches') {
                if (!benchSalesNavAllowed) return null;
              } else if (!hasModuleAccess(item.module)) return null;
              if (item.path === '/camera' && !isAdmin && cameraMenuEligible !== true) return null;
              const c = sectionColors.myItems;
              const basePath = location.pathname.replace(/\/$/, '') || '/';
              const act =
                item.path === '/activities'
                  ? basePath === '/activities'
                  : item.path === '/my-items/pto'
                    ? basePath.startsWith('/my-items/pto')
                    : item.path === '/my-items/my-account-status'
                      ? basePath.startsWith('/my-items/my-account-status')
                      : item.path === '/my-items/my-benches'
                        ? basePath.startsWith('/my-items/my-benches')
                        : isActive(item.path);
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() =>
                      item.path.startsWith('/training') ? openTrainingPath(item.path) : handleNavigation(item.path)
                    }
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                      '&:hover': {
                        backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: act ? 600 : 500,
                        color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
        </Box>

        {canViewAllActivities && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.reports)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setActivityOpen(!activityOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  backgroundColor: isActive('/activities/all')
                    ? alpha(sectionColors.reports, 0.12)
                    : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: sectionColors.reports }}>
                  <ActivitiesIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Activity"
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: sectionColors.reports,
                  }}
                />
                {activityOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.reports }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.reports }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={activityOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigation('/activities/all')}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor: isActive('/activities/all')
                        ? alpha(sectionColors.reports, itemStyle.rowActiveBgAlpha)
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: isActive('/activities/all')
                          ? alpha(sectionColors.reports, itemStyle.rowActiveHoverAlpha)
                          : alpha(sectionColors.reports, itemStyle.rowHoverMutedAlpha),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: isActive('/activities/all')
                          ? sectionColors.reports
                          : alpha(sectionColors.reports, itemStyle.inactiveAlpha),
                      }}
                    >
                      <ActivitiesIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="All activities"
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: isActive('/activities/all') ? 600 : 500,
                        color: isActive('/activities/all')
                          ? sectionColors.reports
                          : alpha(sectionColors.reports, itemStyle.inactiveTextAlpha),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </Box>
        )}

        {showMyTeamSection && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.myTeam)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setMyTeamOpen(!myTeamOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  backgroundColor: myTeamNavItems.some((item) => isActive(item.path))
                    ? alpha(sectionColors.myTeam, 0.12)
                    : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: sectionColors.myTeam }}>
                  <MyTeamIcon />
                </ListItemIcon>
                <ListItemText
                  primary="My Team"
                  secondary={
                    isAdmin && myTeamReporteeCount === 0
                      ? 'Team tools (no reportees required for admin)'
                      : undefined
                  }
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: sectionColors.myTeam,
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { display: 'block', mt: 0.25, color: alpha(sectionColors.myTeam, 0.75) },
                  }}
                />
                {myTeamOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.myTeam }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.myTeam }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={myTeamOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {myTeamNavItems.map((item) => {
                  if (item.path === '/my-team/bench') {
                    if (!benchSalesNavAllowed) return null;
                  } else if (!hasModuleAccess(item.module)) return null;
                  const c = sectionColors.myTeam;
                  const act = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                          '&:hover': {
                            backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: act ? 600 : 500,
                            color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                {myTeamReporteeCount > 0 && !hasAnyMyTeamModule && !isAdmin && (
                  <ListItem disablePadding sx={{ mb: 0.5, pl: 1, pr: 1 }}>
                    <ListItemText
                      primary="My Team pages need access"
                      secondary="Ask an admin to add Jobs (e.g. jobs_view) and/or Camera permissions to your role."
                      primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </Box>
        )}

        {hasModuleAccess('okr') && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.okr)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setOkrMenuOpen(!okrMenuOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  backgroundColor:
                    okrNavItems.some((item) => isActive(item.path)) ||
                    okrSettingsNavItems.some((item) => isActive(item.path))
                      ? alpha(sectionColors.okr, 0.12)
                      : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: sectionColors.okr }}>
                  <OkrMenuIcon />
                </ListItemIcon>
                <ListItemText
                  primary="OKRs"
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: sectionColors.okr,
                  }}
                />
                {okrMenuOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.okr }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.okr }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={okrMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {okrNavItems.map((item) => {
                  if (!hasModuleAccess(item.module)) return null;
                  const c = sectionColors.okr;
                  const act = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                          '&:hover': { backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha) },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>
                          <OkrMenuIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: act ? 600 : 500,
                            color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => setOkrSettingsMenuOpen(!okrSettingsMenuOpen)}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor: okrSettingsNavItems.some((item) => isActive(item.path))
                        ? alpha(sectionColors.okr, 0.1)
                        : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: alpha(sectionColors.okr, 0.85) }}>
                      <TuneIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="OKR Settings"
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: okrSettingsNavItems.some((item) => isActive(item.path)) ? 600 : 500,
                        color: sectionColors.okr,
                      }}
                    />
                    {okrSettingsMenuOpen ? (
                      <ExpandLess className="sidebar-nav-expand-chevron" fontSize="small" sx={{ color: sectionColors.okr }} />
                    ) : (
                      <ExpandMore className="sidebar-nav-expand-chevron" fontSize="small" sx={{ color: sectionColors.okr }} />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={okrSettingsMenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    {okrSettingsNavItems.map((item) => {
                      if (!hasModuleAccess(item.module)) return null;
                      const c = sectionColors.okr;
                      const act = isActive(item.path);
                      return (
                        <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleNavigation(item.path)}
                            sx={{
                              borderRadius: 2,
                              py: 0.75,
                              backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                              '&:hover': { backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha) },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>
                              <TuneIcon sx={{ fontSize: '1rem' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={item.title}
                              primaryTypographyProps={{
                                fontSize: '0.8rem',
                                fontWeight: act ? 600 : 500,
                                color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </List>
            </Collapse>
          </Box>
        )}

        {/* Bench Sales (after OKRs, before Help Desk) */}
        {benchSalesNavAllowed && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.bench)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setBenchSalesOpen(!benchSalesOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  backgroundColor: location.pathname.startsWith('/bench-sales')
                    ? alpha(sectionColors.bench, 0.14)
                    : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: sectionColors.bench }}>
                  <BenchIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Bench Sales"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: location.pathname.startsWith('/bench-sales') ? 600 : 500,
                    color: sectionColors.bench,
                  }}
                />
                {benchSalesOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.bench }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.bench }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={benchSalesOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {benchSalesItems.map((item) => {
                  const c = sectionColors.bench;
                  const act = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                          '&:hover': {
                            backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: act ? 600 : 500,
                            color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        )}

        <Box sx={navSectionSurfaceSx(theme, sectionColors.help || sectionColors.helpDesk)}>
        {/* Help (module demos / README — erp4c-video-creator) */}
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => setHelpOpen(!helpOpen)}
            sx={{
              borderRadius: 2,
              py: 1,
              backgroundColor: helpNavItems.some((item) => isActive(item.path))
                ? alpha(sectionColors.help || sectionColors.helpDesk, 0.12)
                : 'transparent',
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: sectionColors.help || sectionColors.helpDesk }}>
              <HelpOutlineIcon />
            </ListItemIcon>
            <ListItemText
              primary="Help"
              primaryTypographyProps={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: sectionColors.help || sectionColors.helpDesk,
              }}
            />
            {helpOpen ? (
              <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.help || sectionColors.helpDesk }} />
            ) : (
              <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.help || sectionColors.helpDesk }} />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={helpOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {helpNavItems.map((item) => {
              if (item.module && !hasModuleAccess(item.module)) return null;
              const c = sectionColors.help || sectionColors.helpDesk;
              const act = isActive(item.path);
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                      '&:hover': {
                        backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: act ? 600 : 500,
                        color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
        </Box>

        <Box sx={navSectionSurfaceSx(theme, sectionColors.helpDesk)}>
        {/* Help Desk */}
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => setHelpDeskOpen(!helpDeskOpen)}
            sx={{
              borderRadius: 2,
              py: 1,
              backgroundColor: helpDeskNavItems.some((item) => isActive(item.path))
                ? alpha(sectionColors.helpDesk, 0.12)
                : 'transparent',
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: sectionColors.helpDesk }}>
              <SupportAgentIcon />
            </ListItemIcon>
            <ListItemText
              primary="Help Desk"
              primaryTypographyProps={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: sectionColors.helpDesk,
              }}
            />
            {helpDeskOpen ? (
              <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.helpDesk }} />
            ) : (
              <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.helpDesk }} />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={helpDeskOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {helpDeskNavItems.map((item) => {
              if (!hasModuleAccess(item.module)) return null;
              if (
                item.needsSupport &&
                !isAdmin &&
                !hasPermission('helpdesk_support') &&
                !hasPermission('helpdesk_modify')
              ) {
                return null;
              }
              const c = sectionColors.helpDesk;
              const act = isActive(item.path);
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                      '&:hover': {
                        backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: act ? 600 : 500,
                        color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
        </Box>

        {/* Immigration */}
        {hasModuleAccess('immigration') && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.immigration)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setImmigrationOpen(!immigrationOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  backgroundColor: immigrationNavItems.some((item) => isActive(item.path))
                    ? alpha(sectionColors.immigration, 0.12)
                    : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: sectionColors.immigration }}>
                  <ImmigrationIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Immigration"
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: sectionColors.immigration,
                  }}
                />
                {immigrationOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.immigration }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.immigration }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={immigrationOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {immigrationNavItems.map((item) => {
                  if (!hasModuleAccess(item.module)) return null;
                  const c = sectionColors.immigration;
                  const act = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                          '&:hover': {
                            backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: act ? 600 : 500,
                            color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        )}

        {/* Training Section */}
        {hasModuleAccess('training') && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.training)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setTrainingOpen(!trainingOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  backgroundColor:
                    !externalTrainingBase && location.pathname.startsWith('/training')
                      ? alpha(sectionColors.training, 0.14)
                      : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: sectionColors.training }}>
                  <TrainingIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Training"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight:
                      !externalTrainingBase && location.pathname.startsWith('/training') ? 600 : 500,
                    color: sectionColors.training,
                  }}
                />
                {trainingOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.training }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.training }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={trainingOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => openTrainingPath('/training/catalog')}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor:
                        location.pathname === '/training/catalog'
                          ? alpha(sectionColors.training, itemStyle.rowActiveBgAlpha)
                          : 'transparent',
                      '&:hover': {
                        backgroundColor:
                          location.pathname === '/training/catalog'
                            ? alpha(sectionColors.training, itemStyle.rowActiveHoverAlpha)
                            : alpha(sectionColors.training, itemStyle.rowHoverMutedAlpha),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color:
                          location.pathname === '/training/catalog'
                            ? sectionColors.training
                            : alpha(sectionColors.training, itemStyle.inactiveAlpha),
                      }}
                    >
                      <TrainingIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Browse Catalog"
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: location.pathname === '/training/catalog' ? 600 : 500,
                        color:
                          location.pathname === '/training/catalog'
                            ? sectionColors.training
                            : alpha(sectionColors.training, itemStyle.inactiveTextAlpha),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => openTrainingPath('/training/instructor')}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor:
                        location.pathname === '/training/instructor'
                          ? alpha(sectionColors.training, itemStyle.rowActiveBgAlpha)
                          : 'transparent',
                      '&:hover': {
                        backgroundColor:
                          location.pathname === '/training/instructor'
                            ? alpha(sectionColors.training, itemStyle.rowActiveHoverAlpha)
                            : alpha(sectionColors.training, itemStyle.rowHoverMutedAlpha),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color:
                          location.pathname === '/training/instructor'
                            ? sectionColors.training
                            : alpha(sectionColors.training, itemStyle.inactiveAlpha),
                      }}
                    >
                      <VideocamIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Instructor Dashboard"
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: location.pathname === '/training/instructor' ? 600 : 500,
                        color:
                          location.pathname === '/training/instructor'
                            ? sectionColors.training
                            : alpha(sectionColors.training, itemStyle.inactiveTextAlpha),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => openTrainingPath('/training/standalone-exams')}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor:
                        location.pathname === '/training/standalone-exams'
                          ? alpha(sectionColors.training, itemStyle.rowActiveBgAlpha)
                          : 'transparent',
                      '&:hover': {
                        backgroundColor:
                          location.pathname === '/training/standalone-exams'
                            ? alpha(sectionColors.training, itemStyle.rowActiveHoverAlpha)
                            : alpha(sectionColors.training, itemStyle.rowHoverMutedAlpha),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color:
                          location.pathname === '/training/standalone-exams'
                            ? sectionColors.training
                            : alpha(sectionColors.training, itemStyle.inactiveAlpha),
                      }}
                    >
                      <QuizIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Standalone exams"
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: location.pathname === '/training/standalone-exams' ? 600 : 500,
                        color:
                          location.pathname === '/training/standalone-exams'
                            ? sectionColors.training
                            : alpha(sectionColors.training, itemStyle.inactiveTextAlpha),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => openTrainingPath('/training/question-bank')}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor:
                        location.pathname === '/training/question-bank'
                          ? alpha(sectionColors.training, itemStyle.rowActiveBgAlpha)
                          : 'transparent',
                      '&:hover': {
                        backgroundColor:
                          location.pathname === '/training/question-bank'
                            ? alpha(sectionColors.training, itemStyle.rowActiveHoverAlpha)
                            : alpha(sectionColors.training, itemStyle.rowHoverMutedAlpha),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color:
                          location.pathname === '/training/question-bank'
                            ? sectionColors.training
                            : alpha(sectionColors.training, itemStyle.inactiveAlpha),
                      }}
                    >
                      <LibraryBooksIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Question bank"
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: location.pathname === '/training/question-bank' ? 600 : 500,
                        color:
                          location.pathname === '/training/question-bank'
                            ? sectionColors.training
                            : alpha(sectionColors.training, itemStyle.inactiveTextAlpha),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => openTrainingPath('/training/group-discussions')}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      backgroundColor: location.pathname.startsWith('/training/group-discussions')
                        ? alpha(sectionColors.training, itemStyle.rowActiveBgAlpha)
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: location.pathname.startsWith('/training/group-discussions')
                          ? alpha(sectionColors.training, itemStyle.rowActiveHoverAlpha)
                          : alpha(sectionColors.training, itemStyle.rowHoverMutedAlpha),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: location.pathname.startsWith('/training/group-discussions')
                          ? sectionColors.training
                          : alpha(sectionColors.training, itemStyle.inactiveAlpha),
                      }}
                    >
                      <ForumIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Group discussions"
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: location.pathname.startsWith('/training/group-discussions') ? 600 : 500,
                        color: location.pathname.startsWith('/training/group-discussions')
                          ? sectionColors.training
                          : alpha(sectionColors.training, itemStyle.inactiveTextAlpha),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                {(hasPermission('training_admin') || hasPermission('training_add')) && (
                  <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => openTrainingPath('/training/admin')}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor:
                            (location.pathname === '/training/admin' || location.pathname === '/training/materials') &&
                            new URLSearchParams(location.search).get('tab') !== 'exams'
                              ? alpha(sectionColors.training, itemStyle.rowActiveBgAlpha)
                              : 'transparent',
                          '&:hover': {
                            backgroundColor:
                              (location.pathname === '/training/admin' || location.pathname === '/training/materials') &&
                              new URLSearchParams(location.search).get('tab') !== 'exams'
                                ? alpha(sectionColors.training, itemStyle.rowActiveHoverAlpha)
                                : alpha(sectionColors.training, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color:
                              (location.pathname.includes('/training/admin') || location.pathname === '/training/materials') &&
                              new URLSearchParams(location.search).get('tab') !== 'exams'
                                ? sectionColors.training
                                : alpha(sectionColors.training, itemStyle.inactiveAlpha),
                          }}
                        >
                          <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Manage Training"
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight:
                              location.pathname === '/training/admin' && new URLSearchParams(location.search).get('tab') !== 'exams'
                                ? 600
                                : 500,
                            color:
                              (location.pathname === '/training/admin' || location.pathname === '/training/materials') &&
                              new URLSearchParams(location.search).get('tab') !== 'exams'
                                ? sectionColors.training
                                : alpha(sectionColors.training, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => openTrainingPath('/training/admin?tab=exams')}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor:
                            location.pathname === '/training/admin' && new URLSearchParams(location.search).get('tab') === 'exams'
                              ? alpha(sectionColors.training, itemStyle.rowActiveBgAlpha)
                              : 'transparent',
                          '&:hover': {
                            backgroundColor:
                              location.pathname === '/training/admin' && new URLSearchParams(location.search).get('tab') === 'exams'
                                ? alpha(sectionColors.training, itemStyle.rowActiveHoverAlpha)
                                : alpha(sectionColors.training, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color:
                              location.pathname === '/training/admin' && new URLSearchParams(location.search).get('tab') === 'exams'
                                ? sectionColors.training
                                : alpha(sectionColors.training, itemStyle.inactiveAlpha),
                          }}
                        >
                          <QuizIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Exams"
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight:
                              location.pathname === '/training/admin' && new URLSearchParams(location.search).get('tab') === 'exams'
                                ? 600
                                : 500,
                            color:
                              location.pathname === '/training/admin' && new URLSearchParams(location.search).get('tab') === 'exams'
                                ? sectionColors.training
                                : alpha(sectionColors.training, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </>
                )}
              </List>
            </Collapse>
          </Box>
        )}

        {/* Reports */}
        <Box sx={navSectionSurfaceSx(theme, sectionColors.reports)}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => setReportsOpen(!reportsOpen)}
            sx={{
              borderRadius: 2,
              py: 1.2,
              backgroundColor: location.pathname.startsWith('/reports')
                ? alpha(sectionColors.reports, 0.14)
                : 'transparent',
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: sectionColors.reports }}>
              <ReportsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Reports"
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: location.pathname.startsWith('/reports') ? 600 : 500,
                color: sectionColors.reports,
              }}
            />
            {reportsOpen ? (
              <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.reports }} />
            ) : (
              <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.reports }} />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 1.5 }}>
            <ListItem disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                dense
                onClick={() => setReportsUserOpen(!reportsUserOpen)}
                sx={{ borderRadius: 1, py: 0.65 }}
              >
                <ListItemText
                      primary="My Report"
                  primaryTypographyProps={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    color: alpha(sectionColors.reports, 0.95),
                  }}
                />
                {reportsUserOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.reports, fontSize: 20 }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.reports, fontSize: 20 }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={reportsUserOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 1, pb: 1 }}>
                {userReportNavItems.map((item) => {
                  const c = sectionColors.reports;
                  const act = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                          '&:hover': {
                            backgroundColor: act
                              ? alpha(c, itemStyle.rowActiveHoverAlpha)
                              : alpha(c, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: act ? 600 : 500,
                            color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>

            {showManagerReportNav && (
              <>
                <ListItem disablePadding sx={{ mb: 0.25 }}>
                  <ListItemButton
                    dense
                    onClick={() => setReportsManagerOpen(!reportsManagerOpen)}
                    sx={{ borderRadius: 1, py: 0.65 }}
                  >
                    <ListItemText
                      primary="Manager Reports"
                      primaryTypographyProps={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        color: alpha(sectionColors.reports, 0.95),
                      }}
                    />
                    {reportsManagerOpen ? (
                      <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.reports, fontSize: 20 }} />
                    ) : (
                      <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.reports, fontSize: 20 }} />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={reportsManagerOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 1, pb: 1 }}>
                    {managerReportNavItems.map((item) => {
                      const c = sectionColors.reports;
                      const act = isActive(item.path);
                      return (
                        <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleNavigation(item.path)}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                              '&:hover': {
                                backgroundColor: act
                                  ? alpha(c, itemStyle.rowActiveHoverAlpha)
                                  : alpha(c, itemStyle.rowHoverMutedAlpha),
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.title}
                              primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: act ? 600 : 500,
                                color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </>
            )}

            {showConfidentialIdleNav && (
              <>
                <ListItem disablePadding sx={{ mb: 0.25 }}>
                  <ListItemButton
                    dense
                    onClick={() => setReportsConfidentialOpen(!reportsConfidentialOpen)}
                    sx={{ borderRadius: 1, py: 0.65 }}
                  >
                    <ListItemText
                      primary="Confidential Reports"
                      primaryTypographyProps={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        color: alpha(sectionColors.reports, 0.95),
                      }}
                    />
                    {reportsConfidentialOpen ? (
                      <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.reports, fontSize: 20 }} />
                    ) : (
                      <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.reports, fontSize: 20 }} />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={reportsConfidentialOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 1, pb: 0.5 }}>
                    {confidentialReportNavItems.map((item) => {
                      const c = sectionColors.reports;
                      const act = isActive(item.path);
                      return (
                        <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleNavigation(item.path)}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                              '&:hover': {
                                backgroundColor: act
                                  ? alpha(c, itemStyle.rowActiveHoverAlpha)
                                  : alpha(c, itemStyle.rowHoverMutedAlpha),
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.title}
                              primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: act ? 600 : 500,
                                color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </>
            )}
          </List>
        </Collapse>
        </Box>

        {/* Email Extractor */}
        {hasModuleAccess('email_extractor') && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.emailExtractor)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setEmailExtractorOpen(!emailExtractorOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  backgroundColor: location.pathname.startsWith('/email-extractor')
                    ? alpha(sectionColors.emailExtractor, 0.14)
                    : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: sectionColors.emailExtractor }}>
                  <EmailExtractorIcon />
                </ListItemIcon>
                <ListItemText
                  primary="📧 Email Extractor"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: location.pathname.startsWith('/email-extractor') ? 600 : 500,
                    color: sectionColors.emailExtractor,
                  }}
                />
                {emailExtractorOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.emailExtractor }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.emailExtractor }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={emailExtractorOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {emailExtractorItems.map((item) => {
                  const c = sectionColors.emailExtractor;
                  const act = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                          '&:hover': {
                            backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: act ? 600 : 500,
                            color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        )}

        {/* Extracted Data */}
        {hasModuleAccess('extracted_data') && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.extractedData)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setExtractedDataOpen(!extractedDataOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  backgroundColor: location.pathname.startsWith('/extracted-data')
                    ? alpha(sectionColors.extractedData, 0.14)
                    : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: sectionColors.extractedData }}>
                  <LibraryBooksIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Extracted Data"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: location.pathname.startsWith('/extracted-data') ? 600 : 500,
                    color: sectionColors.extractedData,
                  }}
                />
                {extractedDataOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.extractedData }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.extractedData }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={extractedDataOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {extractedDataItems.map((item) => {
                  const c = sectionColors.extractedData;
                  const act = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                          '&:hover': {
                            backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: act ? 600 : 500,
                            color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        )}

        {/* Digital Media */}
        <Box sx={navSectionSurfaceSx(theme, sectionColors.digitalMedia)}>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => setDigitalMediaOpen(!digitalMediaOpen)}
              sx={{
                borderRadius: 2,
                py: 1.2,
                backgroundColor: location.pathname.startsWith('/digital-media')
                  ? alpha(sectionColors.digitalMedia, 0.14)
                  : 'transparent',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: sectionColors.digitalMedia }}>
                <SocialMediaIcon />
              </ListItemIcon>
              <ListItemText
                primary="Digital Media"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: location.pathname.startsWith('/digital-media') ? 600 : 500,
                  color: sectionColors.digitalMedia,
                }}
              />
              {digitalMediaOpen ? (
                <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.digitalMedia }} />
              ) : (
                <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.digitalMedia }} />
              )}
            </ListItemButton>
          </ListItem>
          <Collapse in={digitalMediaOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 2 }}>
              {digitalMediaItems.map((item) => {
                const c = sectionColors.digitalMedia;
                const act = isActive(item.path);
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                        '&:hover': {
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: act ? 600 : 500,
                          color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </Box>

        {/* Backup Administration — system admins only; top-level (not under Administration) */}
        {isAdmin && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.admin)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setBackupAdminMenuOpen(!backupAdminMenuOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  backgroundColor: backupAdminNavItems.some((it) => isActive(it.path))
                    ? alpha(sectionColors.admin, 0.08)
                    : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: sectionColors.admin }}>
                  <BackupAdministrationSectionIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Backup Administration"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: sectionColors.admin,
                  }}
                />
                {backupAdminMenuOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={backupAdminMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {backupAdminNavItems.map((item) => {
                  const c = sectionColors.admin;
                  const act = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                          '&:hover': {
                            backgroundColor: act
                              ? alpha(c, itemStyle.rowActiveHoverAlpha)
                              : alpha(c, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: act ? 600 : 500,
                            color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        )}

        {/* Data Deletion Administration — deletion queues (top-level) */}
        {showDataDeletionAdminSection && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.admin)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setDataDeletionAdminMenuOpen(!dataDeletionAdminMenuOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  backgroundColor: visibleDataDeletionAdminItems.some((it) => isActive(it.path))
                    ? alpha(sectionColors.admin, 0.08)
                    : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: sectionColors.admin }}>
                  <DataDeletionAdminSectionIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Data Deletion Administration"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: sectionColors.admin,
                  }}
                />
                {dataDeletionAdminMenuOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={dataDeletionAdminMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {visibleDataDeletionAdminItems.map((item) => {
                  const c = sectionColors.admin;
                  const act = isActive(item.path);
                  const IconComp = item.ItemIcon;
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                          '&:hover': {
                            backgroundColor: act
                              ? alpha(c, itemStyle.rowActiveHoverAlpha)
                              : alpha(c, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>
                          <IconComp />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: act ? 600 : 500,
                            color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        )}

        {/* Admin Section */}
        {(isAdmin ||
          hasModuleAccess('users') ||
          hasModuleAccess('roles') ||
          hasModuleAccess('groups') ||
          hasPermission('camera_watch') ||
          hasPermission('accounts_approve') ||
          hasPermission('accounts_modify') ||
          hasPermission('helpdesk_admin')) && (
          <Box sx={navSectionSurfaceSx(theme, sectionColors.admin)}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setAdminOpen(!adminOpen)}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  backgroundColor: adminOpen ? alpha(sectionColors.admin, 0.08) : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: sectionColors.admin }}>
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText
                  primary="General Administration"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: sectionColors.admin,
                  }}
                />
                {adminOpen ? (
                  <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                ) : (
                  <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={adminOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {hasModuleAccess('users') && (
                  <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => setEmployeeSettingsOpen(!employeeSettingsOpen)}
                        sx={{ borderRadius: 2, py: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: alpha(sectionColors.admin, 0.85) }}>
                          <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Employee Settings"
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: sectionColors.admin,
                          }}
                        />
                        {employeeSettingsOpen ? (
                          <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        ) : (
                          <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={employeeSettingsOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        {employeeSettingsItems.map((item) => {
                          const c = sectionColors.admin;
                          const act = isActive(item.path);
                          return (
                            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                              <ListItemButton
                                onClick={() => handleNavigation(item.path)}
                                sx={{
                                  borderRadius: 2,
                                  py: 1,
                                  backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                                  '&:hover': { backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha) },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.title}
                                  primaryTypographyProps={{
                                    fontSize: '0.85rem',
                                    fontWeight: act ? 600 : 500,
                                    color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                )}
                {(isAdmin || hasPermission('training_admin')) && (
                  <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => setTrainingAdminOpen(!trainingAdminOpen)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: location.pathname.startsWith('/admin/training-administration')
                            ? alpha(sectionColors.admin, 0.08)
                            : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: alpha(sectionColors.admin, 0.85) }}>
                          <TrainingIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Training Administration"
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: sectionColors.admin,
                          }}
                        />
                        {trainingAdminOpen ? (
                          <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        ) : (
                          <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={trainingAdminOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        <ListItem disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleNavigation('/admin/training-administration')}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              backgroundColor: isActive('/admin/training-administration')
                                ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: isActive('/admin/training-administration')
                                  ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                  : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: isActive('/admin/training-administration')
                                  ? sectionColors.admin
                                  : alpha(sectionColors.admin, itemStyle.inactiveAlpha),
                              }}
                            >
                              <LibraryBooksIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary="Catalog & editions"
                              primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: isActive('/admin/training-administration') ? 600 : 500,
                                color: isActive('/admin/training-administration')
                                  ? sectionColors.admin
                                  : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    </Collapse>
                  </>
                )}
                {isAdmin && (
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => handleNavigation('/admin/help-setup')}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        backgroundColor: isActive('/admin/help-setup')
                          ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: isActive('/admin/help-setup')
                            ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                            : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: isActive('/admin/help-setup')
                            ? sectionColors.admin
                            : alpha(sectionColors.admin, itemStyle.inactiveAlpha),
                        }}
                      >
                        <HelpOutlineIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Help Setup"
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: isActive('/admin/help-setup') ? 600 : 500,
                          color: isActive('/admin/help-setup')
                            ? sectionColors.admin
                            : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
                {isAdmin && (
                  <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => setCustomizeOpen(!customizeOpen)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: location.pathname.startsWith('/admin/customize')
                            ? alpha(sectionColors.admin, 0.08)
                            : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: alpha(sectionColors.admin, 0.85) }}>
                          <TuneIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Customize"
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: sectionColors.admin,
                          }}
                        />
                        {customizeOpen ? (
                          <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        ) : (
                          <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={customizeOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        {[
                          {
                            title: 'Application settings',
                            path: '/admin/customize/application-settings',
                          },
                          ...(customizationGate?.showCustomizationMenus
                            ? [
                                { title: 'Add fields', path: '/admin/customize/fields' },
                                { title: 'Module relationships', path: '/admin/customize/relationships' },
                              ]
                            : []),
                        ].map((item) => {
                          const c = sectionColors.admin;
                          const act = isActive(item.path);
                          return (
                            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                              <ListItemButton
                                onClick={() => handleNavigation(item.path)}
                                sx={{
                                  borderRadius: 2,
                                  py: 1,
                                  backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                                  '&:hover': {
                                    backgroundColor: act
                                      ? alpha(c, itemStyle.rowActiveHoverAlpha)
                                      : alpha(c, itemStyle.rowHoverMutedAlpha),
                                  },
                                }}
                              >
                                <ListItemText
                                  primary={item.title}
                                  primaryTypographyProps={{
                                    fontSize: '0.85rem',
                                    fontWeight: act ? 600 : 500,
                                    color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                )}
                {showAllStatusesSection && (
                  <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => setAllStatusesOpen(!allStatusesOpen)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: visibleAllStatusesItems.some((item) => isActive(item.path))
                            ? alpha(sectionColors.admin, 0.08)
                            : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: alpha(sectionColors.admin, 0.85) }}>
                          <AllStatusesIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="All Statuses"
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: sectionColors.admin,
                          }}
                        />
                        {allStatusesOpen ? (
                          <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        ) : (
                          <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={allStatusesOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        {visibleAllStatusesItems.map((item) => {
                            const c = sectionColors.admin;
                            const act = isActive(item.path);
                            const IconComp = item.ItemIcon;
                            return (
                              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                                <ListItemButton
                                  onClick={() => handleNavigation(item.path)}
                                  sx={{
                                    borderRadius: 2,
                                    py: 1,
                                    backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                                    '&:hover': {
                                      backgroundColor: act
                                        ? alpha(c, itemStyle.rowActiveHoverAlpha)
                                        : alpha(c, itemStyle.rowHoverMutedAlpha),
                                    },
                                  }}
                                >
                                  <ListItemIcon
                                    sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}
                                  >
                                    <IconComp />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={item.title}
                                    primaryTypographyProps={{
                                      fontSize: '0.85rem',
                                      fontWeight: act ? 600 : 500,
                                      color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                      </List>
                    </Collapse>
                  </>
                )}
                {hasModuleAccess('users') && <Divider sx={{ my: 1 }} />}
                {isAdmin && (
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => handleNavigation('/employees')}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        backgroundColor: isActive('/employees')
                          ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: isActive('/employees')
                            ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                            : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: isActive('/employees') ? sectionColors.admin : alpha(sectionColors.admin, itemStyle.inactiveAlpha),
                        }}
                      >
                        <EmployeesIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Employees"
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: isActive('/employees') ? 600 : 500,
                          color: isActive('/employees') ? sectionColors.admin : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
                {showUserSettingsNavSection && (
                  <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => setUserSettingsMenuOpen(!userSettingsMenuOpen)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: userSettingsNavItems.some(
                            (it) => userSettingsNavItemVisible(it) && isActive(it.path)
                          )
                            ? alpha(sectionColors.admin, 0.08)
                            : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: alpha(sectionColors.admin, 0.85) }}>
                          <UserSettingsNavIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="User Settings"
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: sectionColors.admin,
                          }}
                        />
                        {userSettingsMenuOpen ? (
                          <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        ) : (
                          <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={userSettingsMenuOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        {userSettingsNavItems.map((item) => {
                          if (!userSettingsNavItemVisible(item)) return null;
                          const c = sectionColors.admin;
                          const act = isActive(item.path);
                          return (
                            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                              <ListItemButton
                                onClick={() => handleNavigation(item.path)}
                                sx={{
                                  borderRadius: 2,
                                  py: 1,
                                  backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                                  '&:hover': {
                                    backgroundColor: act
                                      ? alpha(c, itemStyle.rowActiveHoverAlpha)
                                      : alpha(c, itemStyle.rowHoverMutedAlpha),
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.title}
                                  primaryTypographyProps={{
                                    fontSize: '0.85rem',
                                    fontWeight: act ? 600 : 500,
                                    color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                )}
                {showSystemsInformationNavSection && (
                  <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => setSystemsInformationMenuOpen(!systemsInformationMenuOpen)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: systemsInformationNavItems.some(
                            (it) => systemsInformationNavItemVisible(it) && isActive(it.path)
                          )
                            ? alpha(sectionColors.admin, 0.08)
                            : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: alpha(sectionColors.admin, 0.85) }}>
                          <SystemsInformationNavIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Systems Information"
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: sectionColors.admin,
                          }}
                        />
                        {systemsInformationMenuOpen ? (
                          <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        ) : (
                          <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={systemsInformationMenuOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        {systemsInformationNavItems.map((item) => {
                          if (!systemsInformationNavItemVisible(item)) return null;
                          const c = sectionColors.admin;
                          const act = isActive(item.path);
                          return (
                            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                              <ListItemButton
                                onClick={() => handleNavigation(item.path)}
                                sx={{
                                  borderRadius: 2,
                                  py: 1,
                                  backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                                  '&:hover': {
                                    backgroundColor: act
                                      ? alpha(c, itemStyle.rowActiveHoverAlpha)
                                      : alpha(c, itemStyle.rowHoverMutedAlpha),
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.title}
                                  primaryTypographyProps={{
                                    fontSize: '0.85rem',
                                    fontWeight: act ? 600 : 500,
                                    color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                )}
                {adminItems.map((item) => {
                  if (item.permissionsAny) {
                    if (!isAdmin && !item.permissionsAny.some((p) => hasPermission(p))) return null;
                  } else if (item.permission) {
                    if (!hasPermission(item.permission)) return null;
                  } else if (!isAdmin && !hasModuleAccess(item.module)) return null;
                  const c = sectionColors.admin;
                  const act = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: act ? alpha(c, itemStyle.rowActiveBgAlpha) : 'transparent',
                          '&:hover': {
                            backgroundColor: act ? alpha(c, itemStyle.rowActiveHoverAlpha) : alpha(c, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: act ? c : alpha(c, itemStyle.inactiveAlpha) }}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: act ? 600 : 500,
                            color: act ? c : alpha(c, itemStyle.inactiveTextAlpha),
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
            {(isAdmin || hasPermission('helpdesk_admin')) && (
              <>
                <Divider sx={{ my: 2 }} />
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => setDataAdminOpen(!dataAdminOpen)}
                    sx={{
                      borderRadius: 2,
                      py: 1.2,
                      backgroundColor: dataAdminOpen ? alpha(sectionColors.admin, 0.08) : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: sectionColors.admin }}>
                      <DataAdminSectionIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Data Administration"
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: sectionColors.admin,
                      }}
                    />
                    {dataAdminOpen ? (
                      <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                    ) : (
                      <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={dataAdminOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => setSeedDataOpen(!seedDataOpen)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          backgroundColor:
                            isActive('/admin/seed-data') || isActive('/admin/helpdesk-severities')
                              ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                              : 'transparent',
                          '&:hover': {
                            backgroundColor:
                              isActive('/admin/seed-data') || isActive('/admin/helpdesk-severities')
                                ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color:
                              isActive('/admin/seed-data') || isActive('/admin/helpdesk-severities')
                                ? sectionColors.admin
                                : alpha(sectionColors.admin, itemStyle.inactiveAlpha),
                          }}
                        >
                          <SeedDataIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="SEED Data"
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight:
                              isActive('/admin/seed-data') || isActive('/admin/helpdesk-severities') ? 600 : 500,
                            color:
                              isActive('/admin/seed-data') || isActive('/admin/helpdesk-severities')
                                ? sectionColors.admin
                                : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                          }}
                        />
                        {seedDataOpen ? (
                          <ExpandLess className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        ) : (
                          <ExpandMore className="sidebar-nav-expand-chevron" sx={{ color: sectionColors.admin }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={seedDataOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        {isAdmin && (
                          <ListItem disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                              onClick={() => handleNavigation('/admin/seed-data')}
                              sx={{
                                borderRadius: 2,
                                py: 1,
                                backgroundColor: isActive('/admin/seed-data')
                                  ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: isActive('/admin/seed-data')
                                    ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                    : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 36,
                                  color: isActive('/admin/seed-data')
                                    ? sectionColors.admin
                                    : alpha(sectionColors.admin, itemStyle.inactiveAlpha),
                                }}
                              >
                                <SeedDataIcon />
                              </ListItemIcon>
                              <ListItemText
                                primary="Seed catalog"
                                primaryTypographyProps={{
                                  fontSize: '0.85rem',
                                  fontWeight: isActive('/admin/seed-data') ? 600 : 500,
                                  color: isActive('/admin/seed-data')
                                    ? sectionColors.admin
                                    : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        )}
                        {(isAdmin || hasPermission('helpdesk_admin')) && (
                          <ListItem disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                              onClick={() => handleNavigation('/admin/helpdesk-severities')}
                              sx={{
                                borderRadius: 2,
                                py: 1,
                                backgroundColor: isActive('/admin/helpdesk-severities')
                                  ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: isActive('/admin/helpdesk-severities')
                                    ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                    : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 36,
                                  color: isActive('/admin/helpdesk-severities')
                                    ? sectionColors.admin
                                    : alpha(sectionColors.admin, itemStyle.inactiveAlpha),
                                }}
                              >
                                <SettingsIcon />
                              </ListItemIcon>
                              <ListItemText
                                primary="Help desk severities"
                                primaryTypographyProps={{
                                  fontSize: '0.85rem',
                                  fontWeight: isActive('/admin/helpdesk-severities') ? 600 : 500,
                                  color: isActive('/admin/helpdesk-severities')
                                    ? sectionColors.admin
                                    : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        )}
                      </List>
                    </Collapse>
                    {isAdmin && (
                      <>
                        <ListItem disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleNavigation('/admin/deleted-data')}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              backgroundColor: isActive('/admin/deleted-data')
                                ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: isActive('/admin/deleted-data')
                                  ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                  : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: isActive('/admin/deleted-data')
                                  ? sectionColors.admin
                                  : alpha(sectionColors.admin, itemStyle.inactiveAlpha),
                              }}
                            >
                              <DeletedDataNavIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary="Deleted Data"
                              primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: isActive('/admin/deleted-data') ? 600 : 500,
                                color: isActive('/admin/deleted-data')
                                  ? sectionColors.admin
                                  : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleNavigation('/admin/archived-data')}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              backgroundColor: isActive('/admin/archived-data')
                                ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: isActive('/admin/archived-data')
                                  ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                  : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: isActive('/admin/archived-data')
                                  ? sectionColors.admin
                                  : alpha(sectionColors.admin, itemStyle.inactiveAlpha),
                              }}
                            >
                              <ArchiveNavIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary="Archived Data"
                              primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: isActive('/admin/archived-data') ? 600 : 500,
                                color: isActive('/admin/archived-data')
                                  ? sectionColors.admin
                                  : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleNavigation('/admin/data-backups')}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              backgroundColor: isActive('/admin/data-backups')
                                ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: isActive('/admin/data-backups')
                                  ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                  : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: isActive('/admin/data-backups')
                                  ? sectionColors.admin
                                  : alpha(sectionColors.admin, itemStyle.inactiveAlpha),
                              }}
                            >
                              <BackupZipNavIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary="Backed Up Data"
                              primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: isActive('/admin/data-backups') ? 600 : 500,
                                color: isActive('/admin/data-backups')
                                  ? sectionColors.admin
                                  : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                        {showDemoDataInUi ? (
                          <ListItem disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                              onClick={() => handleNavigation('/admin/demo-users-view')}
                              sx={{
                                borderRadius: 2,
                                py: 1,
                                backgroundColor: demoUsersViewActive
                                  ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: demoUsersViewActive
                                    ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                    : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36, color: sectionColors.admin }}>
                                <DemoUsersViewIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Demo/Test Users Actual View"
                                primaryTypographyProps={{
                                  fontSize: '0.85rem',
                                  fontWeight: demoUsersViewActive ? 600 : 500,
                                  color: demoUsersViewActive
                                    ? sectionColors.admin
                                    : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ) : null}
                        {showDemoDataInUi ? (
                          <ListItem disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                              onClick={() => handleNavigation('/admin/data-clearance#demo')}
                              sx={{
                                borderRadius: 2,
                                py: 1,
                                backgroundColor: dataClearanceActive
                                  ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: dataClearanceActive
                                    ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                    : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36, color: sectionColors.admin }}>
                                <ClearDemoNavIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Clear Demo/Test Data"
                                primaryTypographyProps={{
                                  fontSize: '0.85rem',
                                  fontWeight: dataClearanceActive ? 600 : 500,
                                  color: dataClearanceActive
                                    ? sectionColors.admin
                                    : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ) : null}
                        <ListItem disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleNavigation('/admin/data-clearance#seed')}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              backgroundColor: dataClearanceActive
                                ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: dataClearanceActive
                                  ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                  : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: theme.palette.warning.main }}>
                              <ClearSeedNavIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Clear Seed Data"
                              primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: dataClearanceActive ? 600 : 500,
                                color: dataClearanceActive
                                  ? sectionColors.admin
                                  : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleNavigation('/admin/data-clearance#all')}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              backgroundColor: dataClearanceActive
                                ? alpha(sectionColors.admin, itemStyle.rowActiveBgAlpha)
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: dataClearanceActive
                                  ? alpha(sectionColors.admin, itemStyle.rowActiveHoverAlpha)
                                  : alpha(sectionColors.admin, itemStyle.rowHoverMutedAlpha),
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: theme.palette.error.main }}>
                              <ClearAllNavIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Clear All Data"
                              primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: dataClearanceActive ? 600 : 500,
                                color: dataClearanceActive
                                  ? sectionColors.admin
                                  : alpha(sectionColors.admin, itemStyle.inactiveTextAlpha),
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      </>
                    )}
                  </List>
                </Collapse>
              </>
            )}
          </Box>
        )}
          </>
        )}
      </List>

      {/* Appearance: templates + brightness / contrast / text scale */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: navCompact && !isMobile ? 'none' : 'block',
        }}
      >
        <ListItemButton
          onClick={() => setAppearanceOpen(!appearanceOpen)}
          sx={{ borderRadius: 2, py: 0.75, px: 1, mb: appearanceOpen ? 1 : 0 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <TuneIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Appearance"
            secondary="Template, brightness, text size"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
          {appearanceOpen ? (
            <ExpandLess className="sidebar-nav-expand-chevron" />
          ) : (
            <ExpandMore className="sidebar-nav-expand-chevron" />
          )}
        </ListItemButton>
        <Collapse in={appearanceOpen} timeout="auto" unmountOnExit>
          <Stack spacing={1.75}>
            <FormControl size="small" fullWidth>
              <InputLabel id="erp-appearance-template">Color template</InputLabel>
              <Select
                labelId="erp-appearance-template"
                label="Color template"
                value={themeMode}
                onChange={(e) => setThemeMode(e.target.value)}
              >
                {themeTemplateOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    <Box>
                      <Typography variant="body2">{opt.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.hint}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Brightness ({themeBrightness}%)
              </Typography>
              <Slider
                size="small"
                value={themeBrightness}
                min={70}
                max={130}
                step={1}
                valueLabelDisplay="auto"
                onChange={(_, v) => setThemeBrightness(v)}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Contrast ({themeContrast}%)
              </Typography>
              <Slider
                size="small"
                value={themeContrast}
                min={85}
                max={120}
                step={1}
                valueLabelDisplay="auto"
                onChange={(_, v) => setThemeContrast(v)}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Text size ({uiFontScale}%)
              </Typography>
              <Slider
                size="small"
                value={uiFontScale}
                min={90}
                max={115}
                step={1}
                valueLabelDisplay="auto"
                onChange={(_, v) => setUiFontScale(v)}
              />
            </Box>
            <Button variant="outlined" size="small" onClick={resetAppearance} fullWidth>
              Reset appearance
            </Button>
          </Stack>
        </Collapse>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: navCompact && !isMobile ? 1 : 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: navCompact && !isMobile ? 'none' : 'block',
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ px: 0.5 }}>
          {footerTextShort}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: desktopDrawerWidth },
        flexShrink: { md: 0 },
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shortest,
        }),
      }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidthExpanded,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: desktopDrawerWidth,
            ...drawerPaperExtraSx,
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
